import { httpResource, HttpResourceRef } from '@angular/common/http';
import {
	inject,
	Injectable,
	linkedSignal,
	Resource,
	resourceFromSnapshots,
	ResourceSnapshot,
	signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map, startWith } from 'rxjs';

import { fallbackLanguage } from '@valk-nx/core/lib/core';
import { LanguageSwitchHelper } from '@valk-nx/helpers/lib/language/language-switch';
import type { FilterableHotelListData } from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import { CONTENT_SERVICE_URL } from './content.service';

export const DEFAULT_DATA: FilterableHotelListData = {
	hotels: [],
	filters: [],
};

type FiltersList = Record<string, string[]>;
type FilterParams = Record<string, string>;

/**
 * Wraps any Resource so that the previous value is retained while a new
 * request is in-flight, instead of dropping to undefined / defaultValue.
 */
export function withPreviousValue<T>(input: Resource<T>): Resource<T> {
	const derived = linkedSignal<ResourceSnapshot<T>, ResourceSnapshot<T>>({
		source: input.snapshot,
		computation: (snapshot, previous) => {
			if (
				snapshot.status === 'loading' &&
				previous &&
				previous.value.status !== 'error'
			) {
				// Keep the last good value visible while the new request loads
				return {
					status: 'loading' as const,
					value: previous.value.value,
				};
			}
			return snapshot;
		},
	});
	return resourceFromSnapshots(derived);
}

function extractLanguageCode(url: string) {
	const urlSegments = url.split('/').filter(Boolean);
	return urlSegments[0];
}

@Injectable({ providedIn: 'root' })
export class FilterableListService {
	private readonly serviceUrl = inject(CONTENT_SERVICE_URL, {
		optional: true,
	});
	private readonly router = inject(Router);
	readonly language = toSignal(
		this.router.events.pipe(
			filter((e) => e instanceof NavigationEnd),
			startWith(null),
			map(() => {
				const candidate = extractLanguageCode(this.router.url);
				return LanguageSwitchHelper.isLanguageCode(candidate)
					? candidate
					: fallbackLanguage;
			}),
		),
		{ initialValue: fallbackLanguage },
	);

	filters = signal<FiltersList>({});

	private readonly _hotelListResource: HttpResourceRef<FilterableHotelListData> =
		httpResource<FilterableHotelListData>(
			() => {
				const HOTELS_API_URL = `${this.serviceUrl}vdc/hotels`;
				const params: FilterParams = {};

				// Add filter params in alphabetical order to ensure consistent URL structure for caching
				const sortedFilterKeys = Object.keys(this.filters()).sort(
					(a, b) => a.localeCompare(b),
				);
				for (const key of sortedFilterKeys) {
					params[key] = this.filters()[key].join(',');
				}

				// Add language last
				params['language'] = this.language();
				const url = `${HOTELS_API_URL}?${new URLSearchParams(params).toString()}`;

				return { url, method: 'GET' };
			},
			{
				defaultValue: DEFAULT_DATA,
				parse: (raw) => (raw as { data: FilterableHotelListData }).data,
			},
		);

	readonly hotelList: Resource<FilterableHotelListData> = withPreviousValue(
		this._hotelListResource,
	);
}
